import { AppearanceError, type ContrastProblem } from './errors.js';
import { contrastRatio, MIN_CONTRAST, normaliseHex, ratioText } from './contrast.js';
import { CONTRAST_PAIRS, PASS_PAIRS, THEME_TOKENS, type ThemeToken } from './tokens.js';

/*
 * A theme (docs/26 §2): every colour token for light and for dark, the logo for each, and the Wallet pass colours.
 * The app sets the tokens as CSS variables (`--<token>`) and draws nothing in a colour of its own, so a theme changes
 * everything the customer sees. Contrast is checked here, the same in the admin (live) and on the server (refusing).
 */

export type ThemeColours = Record<ThemeToken, string>;
export interface PassColours { background: string; foreground: string; label: string }
/**
 * The tab bar and the app's "glass" (Saif, 21 Oct 2026): "pill" is a floating frosted pill above the bottom edge, with
 * the same frosted look on the card tile's pills and the code panel's close button; "standard" is the plain bar on the
 * bottom edge and plain pills, nothing translucent.
 */
export const TAB_BAR_STYLES = ['pill', 'standard'] as const;
export type TabBarStyle = (typeof TAB_BAR_STYLES)[number];
export const TAB_BAR_STYLE_NAMES: Record<TabBarStyle, { label: string; sub: string }> = {
  pill: { label: 'Floating glass pill', sub: 'The tabs in a frosted pill above the bottom edge; the card’s buttons and the code panel share the look' },
  standard: { label: 'Standard bar', sub: 'A plain bar along the bottom edge, nothing see-through' },
};
/**
 * The material (Saif, 21 Oct 2026, the design pass): "glass" is the liquid-glass look on the app's surfaces (tiles, the
 * action row, sheets, pills and chips, the tab bar: translucency, blur where a layer sits over content, a hairline border,
 * a specular line along the top, a soft shadow), the brand card staying the one solid object; "flat" is plain surfaces.
 */
export const MATERIALS = ['glass', 'flat'] as const;
export type Material = (typeof MATERIALS)[number];
export const MATERIAL_NAMES: Record<Material, { label: string; sub: string }> = {
  glass: { label: 'Liquid glass', sub: 'Frosted, see-through surfaces with a light edge; the brand card stays solid' },
  flat: { label: 'Flat', sub: 'Plain surfaces, nothing see-through' },
};
/** The page background: plain, a soft pool of the brand colour drifting behind the top of Home and the card page, or that with a fine grain. */
export const BACKGROUNDS = ['plain', 'glow', 'glow_grain'] as const;
export type Background = (typeof BACKGROUNDS)[number];
export const BACKGROUND_NAMES: Record<Background, { label: string; sub: string }> = {
  plain: { label: 'Plain', sub: 'The page colour only' },
  glow: { label: 'Glow', sub: 'A soft pool of the brand colour behind the top of Home and the card page' },
  glow_grain: { label: 'Glow and grain', sub: 'The glow, with a fine grain over the page' },
};
/** Motion: the glow's slow drift, the card's sheen when its page opens and its slight tilt with the phone. Off, everything is still. */
export const MOTION_NAME = { label: 'Motion', sub: 'The glow drifts, the card gleams once and tilts a little with the phone (never under Reduce Motion)' };

export interface ThemeExtras { tabBar: TabBarStyle; material: Material; background: Background; motion: boolean }
export interface Theme extends ThemeExtras {
  light: ThemeColours;
  dark: ThemeColours;
  /** Storage keys of the uploaded logos (PNG or WebP); null draws the built-in Tikkas wordmark. */
  logo: { light: string | null; dark: string | null };
  pass: PassColours;
}
export const DEFAULT_EXTRAS: ThemeExtras = { tabBar: 'pill', material: 'glass', background: 'glow_grain', motion: true };
const readTabBar = (v: unknown): TabBarStyle | null => ((TAB_BAR_STYLES as readonly unknown[]).includes(v) ? (v as TabBarStyle) : null);
const readMaterial = (v: unknown): Material | null => ((MATERIALS as readonly unknown[]).includes(v) ? (v as Material) : null);
const readBackground = (v: unknown): Background | null => ((BACKGROUNDS as readonly unknown[]).includes(v) ? (v as Background) : null);

const coloursFrom = (mode: 'light' | 'dark'): ThemeColours => Object.fromEntries(THEME_TOKENS.map((t) => [t.key, t[mode]])) as ThemeColours;

/** The starting look: Prototype D, as the app draws it today. */
export const DEFAULT_PASS: PassColours = { background: '#f6f4ef', foreground: '#141210', label: '#766e60' };
export const DEFAULT_THEME: Theme = { light: coloursFrom('light'), dark: coloursFrom('dark'), logo: { light: null, dark: null }, pass: DEFAULT_PASS, ...DEFAULT_EXTRAS };

/** Every pair that fails, in both modes and on the pass: empty when the theme may be saved. */
export function contrastProblems(t: Pick<Theme, 'light' | 'dark' | 'pass'>): ContrastProblem[] {
  const out: ContrastProblem[] = [];
  for (const mode of ['light', 'dark'] as const) {
    for (const p of CONTRAST_PAIRS) {
      const fg = t[mode][p.fg], bg = t[mode][p.bg];
      const ratio = contrastRatio(fg, bg);
      if (ratio < MIN_CONTRAST) out.push({ mode, pair: p.id, label: p.label, fg, bg, ratio, needs: MIN_CONTRAST });
    }
  }
  for (const p of PASS_PAIRS) {
    const fg = t.pass[p.fg], bg = t.pass[p.bg];
    const ratio = contrastRatio(fg, bg);
    if (ratio < MIN_CONTRAST) out.push({ mode: 'pass', pair: p.id, label: p.label, fg, bg, ratio, needs: MIN_CONTRAST });
  }
  return out;
}

/** One sentence naming the first failing pair (and how many more), for a toast or the save bar. */
export function contrastMessage(problems: readonly ContrastProblem[]): string | null {
  const p = problems[0];
  if (!p) return null;
  const where = p.mode === 'pass' ? 'On the Wallet pass' : p.mode === 'light' ? 'In light mode' : 'In dark mode';
  const more = problems.length > 1 ? ` (and ${problems.length - 1} more)` : '';
  return `${where}, ${p.label.toLowerCase()} is ${ratioText(p.ratio)}; it needs 4.5:1${more}.`;
}

function readColours(raw: unknown, mode: 'light' | 'dark'): ThemeColours {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const out = {} as ThemeColours;
  for (const t of THEME_TOKENS) {
    const v = normaliseHex(o[t.key]);
    if (!v) throw new AppearanceError(`${mode === 'light' ? 'Light' : 'Dark'} mode: “${t.label}” needs a colour like #1a2b3c`, 'bad_colour');
    out[t.key] = v;
  }
  return out;
}

function readPass(raw: unknown): PassColours {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const out = {} as PassColours;
  for (const k of ['background', 'foreground', 'label'] as const) {
    const v = normaliseHex(o[k]);
    if (!v) throw new AppearanceError(`Wallet pass: the ${k} needs a colour like #1a2b3c`, 'bad_colour');
    out[k] = v;
  }
  return out;
}

/**
 * A theme to save, checked: every token for both modes as #rrggbb, the pass colours, and every contrast pair at 4.5:1
 * or more (refused with every failing pair, code `bad_contrast`). Logos are uploaded on their own, so they're kept.
 */
export function checkTheme(raw: unknown, logo: Theme['logo'] = { light: null, dark: null }, existing: TabBarStyle | Partial<ThemeExtras> = {}): Theme {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const t = { light: readColours(o.light, 'light'), dark: readColours(o.dark, 'dark'), pass: readPass(o.pass) };
  const problems = contrastProblems(t);
  if (problems.length) throw new AppearanceError(contrastMessage(problems)!, 'bad_contrast', problems);
  // The look's extras ride with the theme: sent, each must be one of its values; left out, the theme keeps what it had.
  const keep: ThemeExtras = { ...DEFAULT_EXTRAS, ...(typeof existing === 'string' ? { tabBar: existing } : existing) };
  if (o.tabBar !== undefined && !readTabBar(o.tabBar)) throw new AppearanceError('The tab bar is "pill" or "standard"', 'bad_tab_bar');
  if (o.material !== undefined && !readMaterial(o.material)) throw new AppearanceError('The material is "glass" or "flat"', 'bad_material');
  if (o.background !== undefined && !readBackground(o.background)) throw new AppearanceError('The background is "plain", "glow" or "glow_grain"', 'bad_background');
  if (o.motion !== undefined && typeof o.motion !== 'boolean') throw new AppearanceError('Motion is on or off', 'bad_motion');
  return { ...t, logo, tabBar: readTabBar(o.tabBar) ?? keep.tabBar, material: readMaterial(o.material) ?? keep.material, background: readBackground(o.background) ?? keep.background, motion: typeof o.motion === 'boolean' ? o.motion : keep.motion };
}

/** A stored theme, read leniently: a token it lacks (a new one) or can't read takes the starting value. */
export function storedTheme(raw: unknown): Theme {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const colours = (r: unknown, mode: 'light' | 'dark'): ThemeColours => {
    const c = r && typeof r === 'object' ? (r as Record<string, unknown>) : {};
    return Object.fromEntries(THEME_TOKENS.map((t) => [t.key, normaliseHex(c[t.key]) ?? t[mode]])) as ThemeColours;
  };
  const p = o.pass && typeof o.pass === 'object' ? (o.pass as Record<string, unknown>) : {};
  const l = o.logo && typeof o.logo === 'object' ? (o.logo as Record<string, unknown>) : {};
  const key = (v: unknown) => (typeof v === 'string' && v.startsWith('appearance/') ? v : null);
  return {
    light: colours(o.light, 'light'),
    dark: colours(o.dark, 'dark'),
    logo: { light: key(l.light), dark: key(l.dark) },
    pass: { background: normaliseHex(p.background) ?? DEFAULT_PASS.background, foreground: normaliseHex(p.foreground) ?? DEFAULT_PASS.foreground, label: normaliseHex(p.label) ?? DEFAULT_PASS.label },
    tabBar: readTabBar(o.tabBar) ?? DEFAULT_EXTRAS.tabBar,
    material: readMaterial(o.material) ?? DEFAULT_EXTRAS.material,
    background: readBackground(o.background) ?? DEFAULT_EXTRAS.background,
    motion: typeof o.motion === 'boolean' ? o.motion : DEFAULT_EXTRAS.motion,
  };
}
